import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Clock, Award, ClipboardList, CreditCard, BookOpen, FileText } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CARD_ACCENTS = ["#0A7AFF", "#22C55E", "#FF3636", "#EAFF00"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0C0C10] border border-[#252530] px-3 py-2">
      <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#EAFF00] mb-1">{label}</p>
      <p className="font-['JetBrains_Mono'] text-sm text-[#EEEADF]">{payload[0].value}</p>
    </div>
  );
};

const todayClasses = [
  { subject: "Data Structures", time: "09:00 AM", room: "B-201", faculty: "Dr. Mehta" },
  { subject: "Computer Networks", time: "11:00 AM", room: "A-105", faculty: "Prof. Sharma" },
  { subject: "DBMS Lab", time: "02:00 PM", room: "Lab-3", faculty: "Ms. Nair" },
];

const upcomingExams = [
  { subject: "Data Structures", date: "Mar 12", type: "Mid-Sem", status: "registered" },
  { subject: "AI & ML", date: "Mar 18", type: "End-Sem", status: "hall-ticket-ready" },
];

export default function StudentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const attendanceData = stats?.charts?.attendance || [
    { month: "Aug", pct: 92 }, { month: "Sep", pct: 88 },
    { month: "Oct", pct: 85 }, { month: "Nov", pct: 90 },
    { month: "Dec", pct: 82 }, { month: "Jan", pct: 87 }
  ];

  const subjectScores = stats?.charts?.subjects || [
    { subject: "Math", score: 85 }, { subject: "Physics", score: 78 },
    { subject: "Chemistry", score: 92 }, { subject: "CS", score: 95 }, { subject: "English", score: 88 }
  ];

  const statCards = [
    { label: "Attendance", value: `${stats?.stats?.attendance_percentage || 87.5}%`, sub: "Last 30 days" },
    { label: "CGPA", value: stats?.stats?.cgpa || "8.4", sub: "Current semester" },
    { label: "Assignments Due", value: stats?.stats?.assignments_due || 3, sub: "Submit soon" },
    { label: "Fees Status", value: "Paid", sub: "Semester 5" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="student-dashboard" className="space-y-6">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Semester 5 — Computer Science</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Hello, {user?.name?.split(" ")[0]}.</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`student-stat-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-3">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Attendance Trend</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-5">Monthly percentage</p>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={attendanceData}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="pct" stroke="#22C55E" strokeWidth={2} fill="url(#attGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Subject Performance</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-5">Scores out of 100</p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={subjectScores} layout="vertical">
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis dataKey="subject" type="category" tick={{ fontSize: 9, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} width={65} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="score" fill="#0A7AFF" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="today-classes">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Today's Classes</p>
          <div className="space-y-2">
            {todayClasses.map((cls, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F2EA] border border-[#E2DDD4]">
                <div className="w-1 h-10 bg-[#0A7AFF] flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{cls.subject}</p>
                  <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{cls.faculty} — {cls.room}</p>
                </div>
                <span className="font-['JetBrains_Mono'] text-xs text-[#0A7AFF]">{cls.time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="upcoming-exams">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Upcoming Exams</p>
          <div className="space-y-2">
            {upcomingExams.map((exam, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F2EA] border border-[#E2DDD4]">
                <div className="w-1 h-10 bg-[#FF3636] flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{exam.subject}</p>
                  <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{exam.type} — {exam.date}</p>
                </div>
                <span className={`font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border ${
                  exam.status === "hall-ticket-ready"
                    ? "border-[#22C55E]/30 text-[#22C55E] bg-[#22C55E]/5"
                    : "border-[#0A7AFF]/30 text-[#0A7AFF] bg-[#0A7AFF]/5"
                }`}>
                  {exam.status === "hall-ticket-ready" ? "Hall Ticket" : "Registered"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
